import gql from 'graphql-tag';

import { gqlClient } from 'graphql/gqlClient';
import { GuestStats, Guest, GuestListItem } from 'types/guest.types';
import { QUESTION_RESPONSE_FRAGMENT } from 'types/question-response.types';

type GetEventGuestStatsQueryResponse = {
  guestStats: Pick<
    GuestStats,
    | 'attended'
    | 'confirmed'
    | 'incomplete'
    | 'interested'
    | 'cancelled'
    | 'waitlisted'
    | 'actionRequired'
    | 'invited'
  >;
};

const GET_EVENT_GUEST_STATS_QUERY = gql`
  query GetEventGuestStats($eventId: Int!) {
    guestStats(eventId: $eventId) {
      attended
      confirmed
      incomplete
      interested
      cancelled
      waitlisted
      actionRequired
      invited
    }
  }
`;

type ListGuestsQueryInput = {
  eventId: number;
  statuses?: string[];
  departments?: string[];
  attendanceFilters?: string[];
  searchText?: string;
  currentPage?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: string;
  isPlusOneEnabled?: boolean;
};

type ListGuestsQueryResult = {
  guests: GuestListItem[];
  pages: number;
  totalGuests: number;
  perPage: number;
  page: number;
};

type LegacyListGuestsQueryResponse = {
  guests: {
    guests: Guest[];
    pages: number;
    totalGuests: number;
    perPage: number;
    page: number;
  };
};

const LIST_GUESTS_QUERY = gql`
  query (
    $eventId: Int!
    $statuses: [String]
    $departments: [String]
    $attendanceFilters: [String]
    $searchText: String
    $currentPage: Int
    $pageSize: Int
    $sortBy: String
    $sortOrder: String
  ) {
    guests(
      guestSearch: {
        eventId: $eventId
        statuses: $statuses
        departments: $departments
        attendanceFilters: $attendanceFilters
        searchText: $searchText
        page: $currentPage
        perPage: $pageSize
        sortBy: $sortBy
        sortOrder: $sortOrder
      }
    ) {
      guests {
        gid
        email
        userId
        department
        firstName
        lastName
        status
        isAttended
        zoomStatus
        meetStatus
        surveyResponses
        lastUpdatedAt
        associatedDistrolists {
          id
          name
        }
      }
      pages
      totalGuests
      perPage
      page
    }
  }
`;

type GuestListQueryResponse = {
  guestList: {
    items: GuestListItem[];
    pages: number;
    page: number;
    totalGuests: number;
  };
};

const GUEST_LIST_QUERY = gql`
  query (
    $eventId: Int!
    $statuses: [String]
    $departments: [String]
    $attendanceFilters: [String]
    $searchText: String
    $currentPage: Int
    $pageSize: Int
  ) {
    guestList(
      guestSearch: {
        eventId: $eventId
        statuses: $statuses
        departments: $departments
        attendanceFilters: $attendanceFilters
        searchText: $searchText
        page: $currentPage
        perPage: $pageSize
      }
    ) {
      pages
      page
      totalGuests
      items {
        ... on Guest {
          __typename
          gid
          email
          userId
          department
          firstName
          lastName
          status
          isAttended
          zoomStatus
          meetStatus
          surveyResponses
          lastUpdatedAt
          associatedDistrolists {
            id
            name
          }
        }
        ... on PlusOneGuest {
          __typename
          plusOneId
          eventId
          firstName
          lastName
          status
          isCheckedIn
          questionnaireResponses {
            ...QuestionResponseFields
          }
          owner {
            gid
            firstName
            lastName
          }
          lastUpdatedAt
        }
      }
    }
  }
  ${QUESTION_RESPONSE_FRAGMENT}
`;

type GuestDepartmentsList = string[];

type ListGuestDepartmentsQueryResponse = {
  guestDepartments: { allGuestDepartments: GuestDepartmentsList };
};

const LIST_GUEST_DEPARTMENTS_QUERY = gql`
  query ListGuestDepartments($eventId: Int!) {
    guestDepartments(eventId: $eventId) {
      allGuestDepartments
    }
  }
`;

const eventGuestsApi = {
  getGuestStats: async (
    eventId: number
  ): Promise<GetEventGuestStatsQueryResponse['guestStats']> => {
    return gqlClient
      .request<GetEventGuestStatsQueryResponse>(GET_EVENT_GUEST_STATS_QUERY, { eventId })
      .then((res) => res.guestStats);
  },
  listGuests: async ({
    eventId,
    currentPage = 0,
    pageSize = 10,
    isPlusOneEnabled = false,
    ...restInput
  }: ListGuestsQueryInput): Promise<ListGuestsQueryResult> => {
    if (isPlusOneEnabled) {
      return gqlClient
        .request<GuestListQueryResponse>(GUEST_LIST_QUERY, {
          eventId,
          currentPage,
          pageSize,
          ...restInput
        })
        .then(({ guestList }) => ({
          guests: guestList.items,
          pages: guestList.pages,
          page: guestList.page,
          totalGuests: guestList.totalGuests,
          perPage: pageSize
        }));
    }

    return gqlClient
      .request<LegacyListGuestsQueryResponse>(LIST_GUESTS_QUERY, {
        eventId,
        currentPage,
        pageSize,
        ...restInput
      })
      .then(({ guests }) => ({
        guests: guests.guests as GuestListItem[],
        pages: guests.pages,
        page: guests.page,
        totalGuests: guests.totalGuests,
        perPage: guests.perPage
      }));
  },
  listGuestDepartments: async (eventId: number): Promise<GuestDepartmentsList> => {
    return gqlClient
      .request<ListGuestDepartmentsQueryResponse>(LIST_GUEST_DEPARTMENTS_QUERY, { eventId })
      .then((res) => res.guestDepartments.allGuestDepartments);
  }
};

export { eventGuestsApi };
